import {
  GameState,
  BusinessNode,
  Road,
  POI,
  GhostStallPayload,
} from '../domain/types.ts';
import { Camera } from './camera.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';
import { GhostClient } from '../multiplayer/ghostClient.ts';

export class WorldRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private camera: Camera;

  constructor(canvas: HTMLCanvasElement, camera: Camera) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not get 2D context');
    this.ctx = context;
    this.camera = camera;
  }

  public resize(): void {
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const rect = this.canvas.getBoundingClientRect();

    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);

    // Hard rule: Chỉ dùng setTransform sau resize, không ctx.scale() lặp lại
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.camera.resize(rect.width, rect.height);
  }

  public render(state: Readonly<GameState>): void {
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    // 1. Clear background
    this.ctx.fillStyle = GAME_CONFIG.render.colors.background;
    this.ctx.fillRect(0, 0, width, height);

    if (!state.neighborhood) return;

    // 2. Render 500m radius boundary
    this.renderBoundary();

    // 2.5 Render Water Feature (Hồ nước / Bờ sông nếu có)
    if (state.neighborhood.waterFeature) {
      this.renderWaterFeature(state.neighborhood.waterFeature);
    }

    // 3. Render roads
    this.renderRoads(state.neighborhood.roads);

    // 4. Render POIs
    this.renderPOIs(state.neighborhood.pois);

    // 5. Render Business Nodes & Your Stalls
    this.renderNodes(state.neighborhood.nodes, state);

    // 6. Render Ghost Rivals (Async Geo Multiplayer)
    this.renderGhosts(state.ghostStalls, state);

    // 7. Render Customers (Người đi bộ, học sinh, shipper, khách ngồi quán)
    if (state.visualCustomers && state.visualCustomers.length > 0) {
      this.renderCustomers(state.visualCustomers);
    }
  }

  private renderWaterFeature(wf: import('../domain/types.ts').WaterFeature): void {
    if (wf.points.length < 2) return;
    this.ctx.save();

    if (wf.type === 'lake') {
      // Vẽ hồ nước dạng Polygon kín
      this.ctx.beginPath();
      const first = this.camera.worldToScreen(wf.points[0]!);
      this.ctx.moveTo(first.x, first.y);
      for (let i = 1; i < wf.points.length; i++) {
        const pt = this.camera.worldToScreen(wf.points[i]!);
        this.ctx.lineTo(pt.x, pt.y);
      }
      this.ctx.closePath();

      this.ctx.fillStyle = 'rgba(0, 150, 136, 0.22)';
      this.ctx.fill();
      this.ctx.strokeStyle = 'rgba(77, 208, 225, 0.5)';
      this.ctx.lineWidth = 1.5;
      this.ctx.stroke();

      // Tên hồ nước ở trung tâm
      const centerPos = this.camera.worldToScreen({ x: 0, y: 0 });
      this.ctx.font = 'bold 11px sans-serif';
      this.ctx.fillStyle = 'rgba(128, 222, 234, 0.8)';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(wf.name || 'Hồ Hoàn Kiếm 🌊', centerPos.x, centerPos.y);

    } else if (wf.type === 'river') {
      // Vẽ dòng sông lớn vắt dọc bản đồ
      this.ctx.beginPath();
      const first = this.camera.worldToScreen(wf.points[0]!);
      this.ctx.moveTo(first.x, first.y);
      for (let i = 1; i < wf.points.length; i++) {
        const pt = this.camera.worldToScreen(wf.points[i]!);
        this.ctx.lineTo(pt.x, pt.y);
      }
      this.ctx.strokeStyle = 'rgba(3, 169, 244, 0.2)';
      this.ctx.lineWidth = Math.max(50, 85 * this.camera.zoom);
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.stroke();

      // Nhãn dòng sông
      const midIdx = Math.floor(wf.points.length / 2);
      const midPoint = wf.points[midIdx] ?? { x: 60, y: 0 };
      const midScreen = this.camera.worldToScreen(midPoint);
      this.ctx.font = 'bold 12px sans-serif';
      this.ctx.fillStyle = 'rgba(129, 212, 250, 0.7)';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(wf.name || 'Sông Hàn ⛵', midScreen.x, midScreen.y);
    }

    this.ctx.restore();
  }

  private renderCustomers(customers: import('../simulation/customerAggregate.ts').VisualCustomer[]): void {
    this.ctx.save();

    for (const c of customers) {
      const pos = this.camera.worldToScreen({ x: c.x, y: c.y });

      // Vẽ nhân vật Chibi Dot
      this.ctx.beginPath();
      const radius = c.state === 'seated' ? 4.5 : 5.5;
      this.ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
      this.ctx.fillStyle = c.color;
      
      // Bóng đổ giúp nổi bật trên nền tối
      this.ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      this.ctx.shadowBlur = 3;
      this.ctx.shadowOffsetY = 1;
      this.ctx.fill();
      this.ctx.shadowColor = 'transparent';

      // Viền đen/trắng giúp nét căng
      this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      this.ctx.lineWidth = 1.2;
      this.ctx.stroke();

      // Icon trạng thái nếu đang ngồi uống nước
      if (c.state === 'seated') {
        this.ctx.font = '8px sans-serif';
        this.ctx.fillText('🥤', pos.x - 2, pos.y - 5);
      } else if (c.state === 'takeaway') {
        // Trạng thái mua mang đi
        this.ctx.font = 'bold 8px sans-serif';
        this.ctx.fillStyle = '#FFD54F';
        this.ctx.textAlign = 'center';
        this.ctx.fillText('🥤 Mang đi', pos.x, pos.y - 7);
      }

      // Bong bóng hội thoại comic cho khách (Google Trends Gossip)
      if (c.chatBubble && Date.now() < c.chatBubble.expiresAt) {
        this.ctx.save();
        this.ctx.font = '8px sans-serif';
        const textWidth = this.ctx.measureText(c.chatBubble.text).width;
        const boxWidth = textWidth + 10;
        const boxHeight = 14;
        const bubbleCenterX = pos.x;
        const bubbleBottomY = pos.y - 12;
        const bubbleTopY = bubbleBottomY - boxHeight;
        const bubbleLeftX = bubbleCenterX - boxWidth / 2;
        const r = 3;

        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
        this.ctx.shadowBlur = 3;
        this.ctx.shadowOffsetY = 1;
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        this.ctx.strokeStyle = '#333333';
        this.ctx.lineWidth = 0.8;

        this.ctx.beginPath();
        this.ctx.moveTo(bubbleLeftX + r, bubbleTopY);
        this.ctx.lineTo(bubbleLeftX + boxWidth - r, bubbleTopY);
        this.ctx.quadraticCurveTo(bubbleLeftX + boxWidth, bubbleTopY, bubbleLeftX + boxWidth, bubbleTopY + r);
        this.ctx.lineTo(bubbleLeftX + boxWidth, bubbleBottomY - r);
        this.ctx.quadraticCurveTo(bubbleLeftX + boxWidth, bubbleBottomY, bubbleLeftX + boxWidth - r, bubbleBottomY);
        // Mũi nhọn comic chỉ xuống đầu khách
        this.ctx.lineTo(bubbleCenterX + 3, bubbleBottomY);
        this.ctx.lineTo(bubbleCenterX, bubbleBottomY + 4);
        this.ctx.lineTo(bubbleCenterX - 3, bubbleBottomY);
        this.ctx.lineTo(bubbleLeftX + r, bubbleBottomY);
        this.ctx.quadraticCurveTo(bubbleLeftX, bubbleBottomY, bubbleLeftX, bubbleBottomY - r);
        this.ctx.lineTo(bubbleLeftX, bubbleTopY + r);
        this.ctx.quadraticCurveTo(bubbleLeftX, bubbleTopY, bubbleLeftX + r, bubbleTopY);
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.stroke();

        this.ctx.shadowColor = 'transparent';
        this.ctx.fillStyle = '#111111';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(c.chatBubble.text, bubbleCenterX, bubbleTopY + boxHeight / 2);
        this.ctx.restore();
      }
    }

    this.ctx.restore();
  }

  private renderBoundary(): void {
    const centerScreen = this.camera.worldToScreen({ x: 0, y: 0 });
    const radiusPx = 500 * this.camera.zoom;

    this.ctx.save();
    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([6, 6]);
    this.ctx.beginPath();
    this.ctx.arc(centerScreen.x, centerScreen.y, radiusPx, 0, Math.PI * 2);
    this.ctx.stroke();

    // Vùng an toàn 500m text
    this.ctx.font = '10px sans-serif';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('Ranh giới địa bàn 500m', centerScreen.x, centerScreen.y - radiusPx - 8);
    this.ctx.restore();
  }

  private renderRoads(roads: Road[]): void {
    this.ctx.save();
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    // 1. Vẽ mặt đường nền
    for (const road of roads) {
      if (road.points.length < 2) continue;
      const isPrimary = road.roadClass === 'primary';
      const isSecondary = road.roadClass === 'secondary';

      this.ctx.lineWidth = isPrimary ? 12 : isSecondary ? 7 : 4;
      this.ctx.strokeStyle = isPrimary ? '#383838' : '#2b2b2b';
      this.ctx.beginPath();
      const first = this.camera.worldToScreen(road.points[0]!);
      this.ctx.moveTo(first.x, first.y);

      for (let i = 1; i < road.points.length; i++) {
        const pt = this.camera.worldToScreen(road.points[i]!);
        this.ctx.lineTo(pt.x, pt.y);
      }
      this.ctx.stroke();
    }

    // 2. Vẽ vạch tim đường vàng đứt đoạn cho trục đường chính
    this.ctx.setLineDash([5, 7]);
    this.ctx.strokeStyle = 'rgba(255, 215, 0, 0.4)';
    this.ctx.lineWidth = 1.2;

    for (const road of roads) {
      if (road.roadClass !== 'primary' || road.points.length < 2) continue;
      this.ctx.beginPath();
      const first = this.camera.worldToScreen(road.points[0]!);
      this.ctx.moveTo(first.x, first.y);

      for (let i = 1; i < road.points.length; i++) {
        const pt = this.camera.worldToScreen(road.points[i]!);
        this.ctx.lineTo(pt.x, pt.y);
      }
      this.ctx.stroke();
    }
    this.ctx.setLineDash([]);

    // 3. Vẽ nhãn tên đường (Street Name Labels)
    // Mặc định chỉ hiện trục đường chính (primary).
    // Khi zoom in (zoomRatio >= 1.35) mới hiện đường nhánh (secondary).
    // Khi zoom in sâu (zoomRatio >= 1.8) mới hiện các ngõ hẻm/đường nhỏ (tertiary/residential).
    const zoomRatio = this.camera.zoom / (this.camera.baseZoom || 1.0);
    this.ctx.font = 'bold 9px sans-serif';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    // Lưu lại vị trí các tên đường đã vẽ để de-duplicate, tránh vẽ 1 tên đường lặp đi lặp lại nhiều lần
    const renderedRoadPositions = new Map<string, { x: number; y: number }[]>();

    for (const road of roads) {
      if (!road.name || road.points.length < 2) continue;

      // Phân cấp hiển thị theo zoom
      const isPrimary = road.roadClass === 'primary';
      const isSecondary = road.roadClass === 'secondary';

      if (!isPrimary) {
        if (!isSecondary && zoomRatio < 1.8) continue;
        if (isSecondary && zoomRatio < 1.35) continue;
      }

      // Tìm đoạn thẳng dài nhất trong tuyến để đặt tên
      let maxDistSq = 0;
      let bestP1 = road.points[0]!;
      let bestP2 = road.points[1]!;

      for (let i = 0; i < road.points.length - 1; i++) {
        const p1 = road.points[i]!;
        const p2 = road.points[i + 1]!;
        const distSq = (p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2;
        if (distSq > maxDistSq) {
          maxDistSq = distSq;
          bestP1 = p1;
          bestP2 = p2;
        }
      }

      // Đoạn quá ngắn (< 35m) thì bỏ qua không vẽ tên
      if (maxDistSq < 1225) continue;

      const s1 = this.camera.worldToScreen(bestP1);
      const s2 = this.camera.worldToScreen(bestP2);
      const midX = (s1.x + s2.x) / 2;
      const midY = (s1.y + s2.y) / 2;

      // De-duplicate: nếu tên đường này đã được vẽ trong bán kính 200px trên màn hình thì bỏ qua
      const existing = renderedRoadPositions.get(road.name);
      if (existing) {
        const tooClose = existing.some(
          (pt) => (pt.x - midX) ** 2 + (pt.y - midY) ** 2 < 200 * 200
        );
        if (tooClose) continue;
        existing.push({ x: midX, y: midY });
      } else {
        renderedRoadPositions.set(road.name, [{ x: midX, y: midY }]);
      }

      let angle = Math.atan2(s2.y - s1.y, s2.x - s1.x);
      // Đảm bảo chữ luôn xuôi chiều đọc (không bị lộn đầu)
      if (angle > Math.PI / 2) angle -= Math.PI;
      else if (angle < -Math.PI / 2) angle += Math.PI;

      this.ctx.save();
      this.ctx.translate(midX, midY);
      this.ctx.rotate(angle);

      // Viền tối halo giúp chữ nổi rõ trên mọi nền bản đồ
      this.ctx.lineWidth = 2.5;
      this.ctx.strokeStyle = 'rgba(15, 15, 25, 0.9)';
      this.ctx.strokeText(road.name, 0, -5);
      this.ctx.fillText(road.name, 0, -5);
      this.ctx.restore();
    }

    this.ctx.restore();
  }

  private renderPOIs(pois: POI[]): void {
    const zoomRatio = this.camera.zoom / (this.camera.baseZoom || 1.0);
    const showPoiNames = zoomRatio >= 1.35; // Chỉ hiện tên quán / POI khi zoom in
    this.ctx.save();

    for (const poi of pois) {
      const pos = this.camera.worldToScreen(poi.position);

      if (poi.type === 'real_stall') {
        // Tiệm thực tế (non-interactive, ambient landmark)
        this.ctx.fillStyle = 'rgba(245, 158, 11, 0.25)';
        this.ctx.beginPath();
        this.ctx.arc(pos.x, pos.y, 9, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.font = '11px sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText('🏪', pos.x, pos.y);

        // Chỉ hiện tên quán khi zoom in
        if (poi.name && showPoiNames) {
          const shortName = poi.name.length > 18 ? poi.name.slice(0, 16) + '...' : poi.name;
          this.ctx.font = 'bold 9px sans-serif';
          this.ctx.textAlign = 'center';
          this.ctx.lineWidth = 2.5;
          this.ctx.strokeStyle = 'rgba(15, 15, 25, 0.9)';
          this.ctx.strokeText(shortName, pos.x, pos.y - 13);
          this.ctx.fillStyle = 'rgba(255, 235, 200, 0.9)';
          this.ctx.fillText(shortName, pos.x, pos.y - 13);
        }
      } else {
        // POI công cộng (trường, chợ, bệnh viện, xe buýt)
        this.ctx.fillStyle = GAME_CONFIG.render.colors.poiMarker;
        this.ctx.beginPath();
        this.ctx.arc(pos.x, pos.y, 6, 0, Math.PI * 2);
        this.ctx.fill();

        if (poi.name && showPoiNames) {
          const shortName = poi.name.length > 18 ? poi.name.slice(0, 16) + '...' : poi.name;
          this.ctx.font = '9px sans-serif';
          this.ctx.textAlign = 'center';
          this.ctx.lineWidth = 2.5;
          this.ctx.strokeStyle = 'rgba(15, 15, 25, 0.9)';
          this.ctx.strokeText(shortName, pos.x, pos.y - 10);
          this.ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
          this.ctx.fillText(shortName, pos.x, pos.y - 10);
        }
      }
    }
    this.ctx.restore();
  }

  private renderNodes(nodes: BusinessNode[], state: Readonly<GameState>): void {
    this.ctx.save();

    for (const node of nodes) {
      const pos = this.camera.worldToScreen({ x: node.x, y: node.y });
      const isOwned = state.player.ownedStalls.some(
        (sid) => state.stalls[sid]?.nodeId === node.id
      );
      const isSelected = state.inspectNodeId === node.id;

      // Selection ring
      if (isSelected) {
        this.ctx.strokeStyle = '#FFFFFF';
        this.ctx.lineWidth = 3;
        this.ctx.beginPath();
        this.ctx.arc(pos.x, pos.y, GAME_CONFIG.render.nodeRadius + 4, 0, Math.PI * 2);
        this.ctx.stroke();
      }

      const rival = !isOwned && state.dummyRivals ? state.dummyRivals[node.id] : undefined;

      // Base Node
      this.ctx.beginPath();
      this.ctx.arc(pos.x, pos.y, GAME_CONFIG.render.nodeRadius, 0, Math.PI * 2);
      if (isOwned) {
        this.ctx.fillStyle = GAME_CONFIG.render.colors.you;
      } else if (rival) {
        if (rival.status === 'booming') {
          this.ctx.fillStyle = '#FF9800'; // Cam rực rỡ ăn nên làm ra
        } else if (rival.status === 'stable') {
          this.ctx.fillStyle = '#4CAF50'; // Xanh lá ổn định
        } else if (rival.status === 'struggling') {
          this.ctx.fillStyle = '#78909C'; // Xám xanh ế ẩm
        } else {
          this.ctx.fillStyle = '#E53935'; // Đỏ vỡ nợ sang 0đ
        }
      } else {
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      }
      this.ctx.fill();

      // Label
      this.ctx.font = 'bold 11px sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      if (isOwned) {
        this.ctx.fillStyle = '#000000';
        this.ctx.fillText('★', pos.x, pos.y);
      } else if (rival) {
        this.ctx.fillStyle = '#FFFFFF';
        this.ctx.fillText(rival.status === 'bankrupt' ? '💀' : '🏪', pos.x, pos.y);
      } else {
        this.ctx.fillStyle = '#FFFFFF';
        this.ctx.fillText('$', pos.x, pos.y);
      }

      // Nếu là quán đã sở hữu, vẽ các ghế đẩu đỏ xung quanh quán (theo số ghế thực tế)
      if (isOwned) {
        const stallId = state.player.ownedStalls.find((sid) => state.stalls[sid]?.nodeId === node.id);
        const stall = stallId ? state.stalls[stallId] : null;
        const chairCount = stall?.chairs ?? 4;
        const orbitRadius = GAME_CONFIG.render.nodeRadius + 9;

        this.ctx.fillStyle = GAME_CONFIG.render.colors.chair; // Đỏ ghế đẩu nhựa vỉa hè
        for (let c = 0; c < chairCount; c++) {
          const angle = (c / chairCount) * Math.PI * 2;
          const cx = pos.x + Math.cos(angle) * orbitRadius;
          const cy = pos.y + Math.sin(angle) * orbitRadius;

          this.ctx.beginPath();
          this.ctx.arc(cx, cy, 2.8, 0, Math.PI * 2);
          this.ctx.fill();
        }
      } else if (rival) {
        // Vẽ ghế nhựa xanh xung quanh quán đối thủ
        const chairCount = 4;
        const orbitRadius = GAME_CONFIG.render.nodeRadius + 8;
        this.ctx.fillStyle = '#1E88E5'; // Ghế nhựa xanh
        for (let c = 0; c < chairCount; c++) {
          const angle = (c / chairCount) * Math.PI * 2;
          const cx = pos.x + Math.cos(angle) * orbitRadius;
          const cy = pos.y + Math.sin(angle) * orbitRadius;

          this.ctx.beginPath();
          this.ctx.arc(cx, cy, 2.4, 0, Math.PI * 2);
          this.ctx.fill();
        }
      }
    }

    this.ctx.restore();
  }

  private renderGhosts(ghosts: GhostStallPayload[], state: Readonly<GameState>): void {
    const zoomRatio = this.camera.zoom / (this.camera.baseZoom || 1.0);
    this.ctx.save();

    for (const ghost of ghosts) {
      const pos = this.camera.worldToScreen({ x: ghost.x, y: ghost.y });
      const isSelected = state.inspectRivalId === ghost.sid;
      const stale = GhostClient.isStale(ghost.activeAt);

      if (isSelected) {
        this.ctx.strokeStyle = '#FFFFFF';
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.arc(pos.x, pos.y, GAME_CONFIG.render.nodeRadius + 3, 0, Math.PI * 2);
        this.ctx.stroke();
      }

      this.ctx.beginPath();
      this.ctx.arc(pos.x, pos.y, GAME_CONFIG.render.nodeRadius, 0, Math.PI * 2);
      this.ctx.fillStyle = stale ? 'rgba(79, 195, 247, 0.4)' : GAME_CONFIG.render.colors.rival;
      this.ctx.fill();

      this.ctx.font = 'bold 10px sans-serif';
      this.ctx.fillStyle = '#000000';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText('👻', pos.x, pos.y);

      // Tên đối thủ: chỉ hiện khi zoom in (zoomRatio >= 1.3) hoặc khi đang được chọn
      if (zoomRatio >= 1.3 || isSelected) {
        const shortGhostName = ghost.name.length > 16 ? ghost.name.slice(0, 14) + '...' : ghost.name;
        this.ctx.font = 'bold 9px sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.lineWidth = 2.5;
        this.ctx.strokeStyle = 'rgba(15, 15, 25, 0.9)';
        this.ctx.strokeText(shortGhostName, pos.x, pos.y + 18);
        this.ctx.fillStyle = 'rgba(79, 195, 247, 0.95)';
        this.ctx.fillText(shortGhostName, pos.x, pos.y + 18);
      }
    }

    this.ctx.restore();
  }
}
